import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { LanguageToggle } from '@/components/LanguageToggle';
import { InstallButton } from '@/components/InstallPrompt';
import { useT } from '@/lib/i18n';
import { APP_VERSION, SUPPORT_EMAIL, PH_HOTLINES } from '@/lib/version';
import { Mail, Phone, Heart } from 'lucide-react';

const Col = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>{title}</h3>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

const FLink = ({ onClick, href, children, testid }) => (
  href ? (
    <a href={href} data-testid={testid} className="text-sm text-left hover:underline" style={{ color: 'var(--text)' }}>{children}</a>
  ) : (
    <button onClick={onClick} data-testid={testid} className="text-sm text-left hover:underline" style={{ color: 'var(--text)' }}>{children}</button>
  )
);

export const SiteFooter = () => {
  const navigate = useNavigate();
  const t = useT();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t mt-8" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }} data-testid="site-footer">
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="grid gap-8 md:grid-cols-4">
          <div className="md:col-span-1">
            <Logo size={28} />
            <p className="text-sm mt-3" style={{ color: 'var(--text-muted)' }}>{t('foot_tagline')}</p>
            <div className="mt-4"><InstallButton className="!min-h-[42px] !px-4 text-sm" label={t('l_install')} testid="footer-install-button" /></div>
          </div>

          <Col title={t('foot_product')}>
            <FLink onClick={() => navigate('/app/start')} testid="footer-start-ride">{t('foot_start')}</FLink>
            <FLink onClick={() => navigate('/app/vehicle')} testid="footer-check-vehicle">{t('foot_check')}</FLink>
            <FLink onClick={() => navigate('/app')} testid="footer-open-app">{t('foot_open')}</FLink>
            <FLink onClick={() => navigate('/install')} testid="footer-install-guide">{t('foot_install_guide')}</FLink>
          </Col>

          <Col title={t('foot_company')}>
            <FLink onClick={() => navigate('/about')} testid="footer-about">{t('foot_story')}</FLink>
            <FLink onClick={() => navigate('/privacy')} testid="footer-privacy">{t('foot_privacy')}</FLink>
            <FLink onClick={() => navigate('/terms')} testid="footer-terms">{t('foot_terms')}</FLink>
          </Col>

          <Col title={t('foot_support')}>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm inline-flex items-center gap-1.5 hover:underline" style={{ color: 'var(--text)' }} data-testid="footer-support-email"><Mail size={14} /> {SUPPORT_EMAIL}</a>
            <div className="mt-1">
              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{t('foot_hotlines')}</div>
              {PH_HOTLINES.map((h) => (
                <a key={h.number} href={`tel:${h.number.replace(/\s/g, '')}`} className="text-sm inline-flex items-center gap-1.5 hover:underline" style={{ color: 'var(--text)' }} data-testid="footer-hotline">
                  <Phone size={13} /> {h.label}: <span className="font-semibold">{h.number}</span>
                </a>
              ))}
            </div>
            <div className="mt-2"><LanguageToggle /></div>
          </Col>
        </div>

        <div className="mt-8 pt-5 border-t flex flex-col sm:flex-row items-center justify-between gap-2" style={{ borderColor: 'var(--border)' }}>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>© {year} KilalaRide · v{APP_VERSION}</p>
          <p className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><Heart size={12} style={{ color: 'var(--primary)' }} /> {t('foot_made')}</p>
        </div>
      </div>
    </footer>
  );
};
